import React, { useEffect, useState } from "react";
import { Activity, Users, Eye, Smartphone, Loader2, CalendarRange } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../../api";
import { AdminVisitorCountries } from "./AdminVisitorCountries";

const fmtDay = (iso) => {
  try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
};

const Tile = ({ icon: Icon, label, value, testId, active, onClick }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`text-left bg-card border rounded-2xl p-4 transition-all ${active ? "border-[#16a34a] ring-2 ring-[#16a34a]/30" : "border-border hover:border-[#16a34a]/50"}`}>
    <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Icon size={14} className="text-[#16a34a]" /> {label}</div>
    <p className="text-2xl font-extrabold text-foreground">{(value ?? 0).toLocaleString()}</p>
  </button>
);

const METRICS = {
  visitors: { label: "Visitors", color: "#2563eb" },
  views: { label: "Page views", color: "#16a34a" },
  installs: { label: "App installs", color: "#b45309" },
  perVisitor: { label: "Views / visitor", color: "#7c3aed" },
};

const List = ({ title, rows, empty, testId }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid={testId}>
      <h4 className="font-bold text-foreground mb-4">{title}</h4>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">{empty}</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.name}>
              <div className="flex justify-between text-sm mb-1 gap-3">
                <span className="font-medium text-foreground truncate">{r.name}</span>
                <span className="text-muted-foreground shrink-0">{r.count.toLocaleString()}</span>
              </div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full bg-[#16a34a]" style={{ width: `${Math.max(4, (r.count / max) * 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const presetLabel = (d) => (d === 1 ? "today" : `the last ${d} days`);

export const AdminTraffic = () => {
  const [metric, setMetric] = useState("visitors");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [params, setParams] = useState({ days: 1 });
  const [preset, setPreset] = useState(1); // default to the day (Today) view; 0 = custom range
  const [showCustom, setShowCustom] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState(todayIso());

  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/traffic", { params })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  const pickPreset = (d) => { setPreset(d); setShowCustom(false); setParams({ days: d }); };
  const applyCustom = () => {
    if (!start || !end) return;
    setPreset(0);
    setParams({ start, end });
  };

  const rangeLabel = params.start
    ? `${fmtDay(params.start)} – ${fmtDay(params.end)}`
    : presetLabel(params.days);

  const trend = (data?.trend || []).map((p) => ({ ...p, label: p.label || fmtDay(p.date) }));

  return (
    <div className="space-y-4" data-testid="admin-traffic">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-extrabold text-foreground flex items-center gap-2"><Activity size={18} className="text-[#16a34a]" /> Site traffic</h3>
          <p className="text-xs text-muted-foreground">Cookie-free: visitors are an IP+browser hash that resets every day. No personal data stored.</p>
        </div>
        <div className="flex items-center gap-1 bg-secondary rounded-xl p-1">
          {[[1, "Today"], [14, "14 days"], [30, "30 days"]].map(([d, label]) => (
            <button key={d} onClick={() => pickPreset(d)} data-testid={`traffic-days-${d}`}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${preset === d ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
          <button onClick={() => setShowCustom((v) => !v)} data-testid="traffic-custom-toggle"
            className={`flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${preset === 0 ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
            <CalendarRange size={13} /> Custom
          </button>
        </div>
      </div>

      {showCustom && (
        <div className="flex flex-wrap items-end gap-3 bg-card border border-border rounded-2xl p-4" data-testid="traffic-custom-range">
          <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground">
            From
            <input type="date" value={start} max={end || todayIso()} onChange={(e) => setStart(e.target.value)}
              data-testid="traffic-custom-start"
              className="bg-secondary border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold text-muted-foreground">
            To
            <input type="date" value={end} min={start} max={todayIso()} onChange={(e) => setEnd(e.target.value)}
              data-testid="traffic-custom-end"
              className="bg-secondary border border-border rounded-lg px-3 py-1.5 text-sm text-foreground" />
          </label>
          <button onClick={applyCustom} disabled={!start || !end} data-testid="traffic-custom-apply"
            className="bg-[#16a34a] text-white text-sm font-bold px-4 py-2 rounded-lg disabled:opacity-40 hover:bg-[#15803d] transition-colors">
            Apply range
          </button>
          <p className="text-xs text-muted-foreground ml-auto self-center">Pick any start and end day — up to a year.</p>
        </div>
      )}

      {loading ? (
        <div className="h-40 rounded-2xl bg-secondary animate-pulse flex items-center justify-center"><Loader2 className="animate-spin text-muted-foreground" /></div>
      ) : !data ? (
        <p className="text-sm text-muted-foreground">Could not load traffic.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile icon={Users} label="Visitors" value={data.visitors} testId="traffic-visitors" active={metric === "visitors"} onClick={() => setMetric("visitors")} />
            <Tile icon={Eye} label="Page views" value={data.pageViews} testId="traffic-pageviews" active={metric === "views"} onClick={() => setMetric("views")} />
            <Tile icon={Smartphone} label="App installs" value={data.installs} testId="traffic-installs" active={metric === "installs"} onClick={() => setMetric("installs")} />
            <Tile icon={Activity} label="Views / visitor" value={data.visitors ? Math.round((data.pageViews / data.visitors) * 10) / 10 : 0} testId="traffic-per-visitor" active={metric === "perVisitor"} onClick={() => setMetric("perVisitor")} />
          </div>

          <div className="bg-card border border-border rounded-2xl p-5" data-testid="traffic-trend">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <h4 className="font-bold text-foreground">{METRICS[metric].label} <span className="text-xs font-normal text-muted-foreground">· tap a tile to switch</span></h4>
              <div className="flex items-center gap-1 bg-secondary rounded-lg p-1" data-testid="traffic-metric-switch">
                {Object.entries(METRICS).map(([k, m]) => (
                  <button key={k} onClick={() => setMetric(k)} data-testid={`traffic-metric-${k}`}
                    className={`text-[11px] font-bold px-2.5 py-1 rounded-md transition-colors ${metric === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            {trend.every((p) => !p[metric]) ? (
              <p className="text-sm text-muted-foreground py-10 text-center">No {METRICS[metric].label.toLowerCase()} recorded yet.</p>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="tv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={METRICS[metric].color} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={METRICS[metric].color} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" />
                    <YAxis tick={{ fontSize: 11 }} stroke="var(--muted-foreground)" allowDecimals={metric === "perVisitor"} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid var(--border)", background: "var(--card)", fontSize: 12 }} />
                    <Area type="monotone" dataKey={metric} name={METRICS[metric].label} stroke={METRICS[metric].color} strokeWidth={2} fill="url(#tv)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <List title="Top pages" rows={data.topPages || []} empty="No page views yet." testId="traffic-top-pages" />
            <List title="Top referrers" rows={data.topReferrers || []} empty="No outside referrers yet — most visits are direct." testId="traffic-top-referrers" />
          </div>

          <AdminVisitorCountries params={params} rangeLabel={rangeLabel} />
        </>
      )}
    </div>
  );
};
